import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Checkbox, FormControlLabel,
  TextField, List, ListItem, ListItemIcon, ListItemText, Divider, Stepper, Step, StepLabel,
} from '@mui/material';
import { CheckCircle, Cancel, WhatsApp, AccountBalanceWallet } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getWalletActivationStatus, requestWalletActivationOtp, activateWallet } from 'api/withdrawal';
import { notifyWalletStatusChanged } from 'hooks/useWalletStatus';

const SUPPORT_WA = '8562077790708';

/**
 * Shown on the wallet page while the customer's wallet is off. Explains why,
 * lists the eligibility requirements, and — when self-activation is enabled
 * and the customer qualifies — walks through: terms → legal name → WhatsApp OTP.
 */
const WalletActivationPanel = ({ customerCode, onActivated }) => {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(-1); // -1 overview, 0 terms, 1 name, 2 code
  const [readToEnd, setReadToEnd] = useState(false);
  const [agree, setAgree] = useState(false);
  const [noInterest, setNoInterest] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [otpRef, setOtpRef] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const termsBox = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getWalletActivationStatus();
      setStatus(r.data);
      setLegalName(r.data.legalName || '');
    } catch (e) {
      setError(e.message || t('walletActivation.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const waLink = `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(t('walletInactive.waMessage', { code: customerCode || '' }))}`;

  const onTermsScroll = () => {
    const el = termsBox.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setReadToEnd(true);
  };
  // Short terms on a tall screen never scroll — treat as read.
  useEffect(() => {
    if (step !== 0) return;
    const el = termsBox.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setReadToEnd(true);
  }, [step]);

  const sendCode = async () => {
    setBusy(true); setError('');
    try {
      const r = await requestWalletActivationOtp();
      setOtpRef(r.data.ref); setOtp('');
    } catch (e) {
      setError(e.message || t('walletActivation.error'));
    } finally { setBusy(false); }
  };

  const errorText = (e) => {
    if (e.code === 'OTP_INVALID') return t('walletActivation.otpInvalid', { left: e.attemptsLeft ?? 0 });
    if (e.code === 'OTP_EXPIRED') return t('walletActivation.otpExpired');
    if (e.code === 'OTP_LOCKED') return t('walletActivation.otpLocked');
    if (e.code === 'TERMS_OUTDATED') return t('walletActivation.termsOutdated');
    return e.message || t('walletActivation.error');
  };

  const activate = async () => {
    setBusy(true); setError('');
    try {
      await activateWallet({
        ref: otpRef, otp, termsVersion: status.termsVersion, legalName: legalName.trim(),
        language: (i18n.language || 'en').split('-')[0],
      });
      notifyWalletStatusChanged(); // sidebar shows the Wallet link right away
      onActivated?.();
    } catch (e) {
      if (e.code === 'TERMS_OUTDATED') { setStep(0); setReadToEnd(false); setAgree(false); load(); }
      setError(errorText(e));
    } finally { setBusy(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>;

  const summary = t('walletTerms.summary', { returnObjects: true });
  const sections = t('walletTerms.sections', { returnObjects: true });

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <AccountBalanceWallet color="secondary" sx={{ mr: 1 }} />
        {t('walletActivation.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>{t('walletActivation.intro')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {step === -1 && status && (
        <>
          {!status.enabled || status.blockedByStaff ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {status.blockedByStaff ? t('walletActivation.blockedByStaff') : t('walletActivation.disabled')}
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight={700}>{t('walletActivation.requirementsTitle')}</Typography>
              <List dense>
                {status.requirements.map((r) => (
                  <ListItem key={r.key} disableGutters>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      {r.met ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </ListItemIcon>
                    <ListItemText primary={t(`walletActivation.req.${r.key}`, { current: r.current, required: r.required, days: r.days })} />
                  </ListItem>
                ))}
              </List>
              {!status.eligible && <Alert severity="warning" sx={{ mb: 2 }}>{t('walletActivation.notEligible')}</Alert>}
            </>
          )}
          <Box display="flex" gap={1.5} flexWrap="wrap">
            {status.enabled && status.eligible && (
              <Button variant="contained" color="secondary" onClick={() => setStep(0)}>{t('walletActivation.start')}</Button>
            )}
            <Button variant="outlined" color="secondary" startIcon={<WhatsApp />} href={waLink} target="_blank" rel="noopener noreferrer">
              {t('walletInactive.askWhatsApp')}
            </Button>
          </Box>
        </>
      )}

      {step >= 0 && (
        <>
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
            <Step><StepLabel>{t('walletActivation.stepTerms')}</StepLabel></Step>
            <Step><StepLabel>{t('walletActivation.stepName')}</StepLabel></Step>
            <Step><StepLabel>{t('walletActivation.stepCode')}</StepLabel></Step>
          </Stepper>

          {step === 0 && (
            <>
              <Box ref={termsBox} onScroll={onTermsScroll} sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 2, mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>{t('walletTerms.title')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('walletTerms.version')}</Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>{t('walletTerms.summaryTitle')}</Typography>
                <ul style={{ marginTop: 4 }}>
                  {Array.isArray(summary) && summary.map((s) => <li key={s}><Typography variant="body2">{s}</Typography></li>)}
                </ul>
                <Divider sx={{ my: 2 }} />
                {Array.isArray(sections) && sections.map((s) => (
                  <Box key={s.title} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{s.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{s.body}</Typography>
                  </Box>
                ))}
              </Box>
              {!readToEnd && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{t('walletActivation.scrollToEnd')}</Typography>}
              <FormControlLabel disabled={!readToEnd} control={<Checkbox color="secondary" checked={agree} onChange={(e) => setAgree(e.target.checked)} />} label={t('walletActivation.termsAgree')} />
              <FormControlLabel disabled={!readToEnd} control={<Checkbox color="secondary" checked={noInterest} onChange={(e) => setNoInterest(e.target.checked)} />} label={t('walletActivation.noInterest')} />
              <Box display="flex" gap={1.5} mt={2}>
                <Button variant="outlined" color="secondary" onClick={() => setStep(-1)}>{t('walletActivation.back')}</Button>
                <Button variant="contained" color="secondary" disabled={!agree || !noInterest} onClick={() => setStep(1)}>{t('walletActivation.continue')}</Button>
              </Box>
            </>
          )}

          {step === 1 && (
            <>
              <Typography variant="subtitle1" fontWeight={700}>{t('walletActivation.nameTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('walletActivation.nameHint')}</Typography>
              <TextField fullWidth label={t('walletActivation.nameLabel')} value={legalName} onChange={(e) => setLegalName(e.target.value)} inputProps={{ maxLength: 120 }} />
              <Box display="flex" gap={1.5} mt={2}>
                <Button variant="outlined" color="secondary" onClick={() => setStep(0)}>{t('walletActivation.back')}</Button>
                <Button variant="contained" color="secondary" disabled={legalName.trim().length < 2} onClick={() => setStep(2)}>{t('walletActivation.continue')}</Button>
              </Box>
            </>
          )}

          {step === 2 && (
            <>
              <Button variant={otpRef ? 'outlined' : 'contained'} color="secondary" startIcon={<WhatsApp />} onClick={sendCode} disabled={busy}>
                {otpRef ? t('walletActivation.resendCode') : t('walletActivation.sendCode')}
              </Button>
              {otpRef && (
                <>
                  <Alert severity="success" sx={{ my: 2 }}>{t('walletActivation.codeSent')}</Alert>
                  <TextField
                    label={t('walletActivation.codeLabel')} value={otp}
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </>
              )}
              <Box display="flex" gap={1.5} mt={2}>
                <Button variant="outlined" color="secondary" onClick={() => setStep(1)}>{t('walletActivation.back')}</Button>
                <Button
                  variant="contained" color="secondary" onClick={activate}
                  disabled={busy || !otpRef || otp.length !== 6}
                  startIcon={busy ? <CircularProgress size={18} /> : <CheckCircle />}
                >
                  {t('walletActivation.activate')}
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default WalletActivationPanel;
